import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AppLayout } from '@/components/app-layout';
import { AuthCheck } from '@/components/auth-check.component';
import { FeatureGate } from '@/components/feature-gate.component';
import { Head } from '@/components/head.component';
import { Feature } from '@/constants/features';
import { AgentWorkspace } from '@/containers/agents/agent-workspace.component';
import { AgentsGate } from '@/containers/agents/agents-gate.component';
import {
  locationOf,
  parseWorkspaceSearch,
  pushesHistory,
  searchOf,
  withChat,
  type WorkspaceLocation,
  type WorkspaceSearch,
} from '@/containers/agents/workspace-location.util';

// The Agent tab is the bare URL; the other tabs, a drill-in (`view`), an open
// run, a card of the play and a play proposal are search params, so each
// links. Opening a drill-in, a run or a proposal pushes, so Back steps out of
// it; switching tabs replaces. Old links: `tab=history` is Runs, `tab=config`
// the Agent tab. The agent's chat open in the dock is `chat`, kept across the
// tabs: opening one pushes, so Back and links work; none is the agent's home.
export const Route = createFileRoute('/agents/$agentId')({
  component: AgentPage,
  validateSearch: (search: Record<string, unknown>): WorkspaceSearch =>
    parseWorkspaceSearch(search),
});

function AgentPage() {
  const { agentId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const location = locationOf(search);

  return (
    <>
      <Head heading='Agents' />
      <AuthCheck>
        <AppLayout variant='full-bleed'>
          <FeatureGate feature={Feature.AGENTS}>
            <AgentsGate>
              {/* Another agent is another page: nothing of the last one's
                  record or edits carries over to it. */}
              <AgentWorkspace
                key={agentId}
                agentId={agentId}
                fresh={search.new}
                location={location}
                onNavigate={(next: WorkspaceLocation) =>
                  void navigate({
                    to: '/agents/$agentId',
                    params: { agentId },
                    search: searchOf(next, search.new, search.chat),
                    replace: !pushesHistory(location, next),
                  })
                }
                chatId={search.chat}
                onOpenChat={(chat, { replace = false } = {}) =>
                  void navigate({
                    to: '/agents/$agentId',
                    params: { agentId },
                    search: withChat(search, chat),
                    replace,
                  })
                }
              />
            </AgentsGate>
          </FeatureGate>
        </AppLayout>
      </AuthCheck>
    </>
  );
}
